import React, { useEffect, useRef, useState } from 'react'
import Input from 'components/input'
import Button from 'components/button'
import Menu from 'components/menu'
import Checkbox from 'components/checkbox'
import InfoHint from 'components/info-hint'
import { Cross, Sort, Drag, Plus, ChevronDown } from 'components/icons'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { IContextMenuState } from 'types'

// Wide enough for a column value without crowding; the menu is right-aligned under the button.
const SUGGESTIONS_MENU_WIDTH = 280

// The label is kept short enough to sit on one line in a 340px panel; the detail lives in the hint.
const EXPAND_OPTIONS_HELP =
  "When enabled, typing a value that isn't already an option into a cell creates it, adds it to this list " +
  'and applies it in one action.'

interface ColumnManualOptionsProps {
  options: string[]
  onAdd: (value: string) => void
  onUpdate: (index: number, value: string) => void
  onRemove: (index: number) => void
  onMove: (from: number, to: number) => void
  onSort: () => void
  // Distinct values the table already holds in this column that aren't options yet.
  suggestions?: string[]
  onAddMany?: (values: string[]) => void
  expandOptionsViaCellEntry?: boolean
  onExpandOptionsChange?: (value: boolean) => void
}

// The hand-entered options of a select column: a count, a sort toggle, a drag-reorderable list of
// directly editable rows, and the buttons that add more. Shared by every surface that configures a
// select column's manual list — the add-column configure stage and the Edit Column menu — so the two
// look and behave identically. Holds only the transient interaction state (the option being named,
// which row is mid-drag, whether the table-values list is open); the options themselves are controlled
// by the consumer, which decides whether an edit goes to an in-memory draft or straight to the API.
const ColumnManualOptions: React.FC<ColumnManualOptionsProps> = ({
  options,
  onAdd,
  onUpdate,
  onRemove,
  onMove,
  onSort,
  suggestions = [],
  onAddMany,
  expandOptionsViaCellEntry,
  onExpandOptionsChange
}) => {
  // The option currently being named: a row at the end of the list that isn't an option yet. Null when
  // there isn't one. Keeping it out of `options` means an abandoned one is never added or persisted.
  const [draft, setDraft] = useState<string | null>(null)
  // Set when the draft row is closed deliberately, so its own blur doesn't then act on it again.
  const settledRef = useRef<boolean>(false)
  // Index of the option currently being dragged by its grip (null when nothing is being dragged).
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [suggestionsMenu, setSuggestionsMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const offersSuggestions = Boolean(onAddMany) && suggestions.length > 0

  // The point of the button is to go straight to typing the name, so the new row takes focus as it
  // appears. Done here rather than with autoFocus, which the a11y lint rule rightly bans.
  const draftOpen = draft !== null
  const draftInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (draftOpen) draftInputRef.current?.focus()
  }, [draftOpen])

  // Enter keeps an empty row open so options can be typed one after another; clicking away closes it.
  const commitDraft = (keepAdding: boolean): void => {
    settledRef.current = true
    const value = (draft ?? '').trim()
    if (value) onAdd(value)
    setDraft(keepAdding ? '' : null)
  }

  const cancelDraft = (): void => {
    settledRef.current = true
    setDraft(null)
  }

  const handleDraftBlur = (): void => {
    if (settledRef.current) {
      settledRef.current = false
      return
    }
    commitDraft(false)
  }

  const handleDrop = (targetIndex: number) => (): void => {
    if (dragIndex !== null) onMove(dragIndex, targetIndex)
    setDragIndex(null)
  }

  const openSuggestions = (event: React.MouseEvent<HTMLButtonElement>): void => {
    const rect = event.currentTarget.getBoundingClientRect()
    setSuggestionsMenu({
      open: true,
      top: `${rect.bottom + 4}px`,
      left: `${rect.right - SUGGESTIONS_MENU_WIDTH}px`,
      right: 'auto',
      bottom: 'auto'
    })
  }

  // Taking one leaves the list open so several can be picked in a row — the value drops out of
  // `suggestions` as the consumer adds it — but the last one closes the list, since the control that
  // opened it is about to disappear with it.
  const takeSuggestion = (value: string): void => {
    onAddMany?.([value])
    if (suggestions.length <= 1) setSuggestionsMenu(INITIAL_CONTEXT_MENU_STATE)
  }

  const takeAllSuggestions = (): void => {
    onAddMany?.(suggestions)
    setSuggestionsMenu(INITIAL_CONTEXT_MENU_STATE)
  }

  return (
    <div className="px-2.5 pt-4">
      <div className="flex items-center mb-2">
        <span className="text-sm text-muted">
          {options.length} option{options.length === 1 ? '' : 's'}
        </span>
        {/* Nothing to order with fewer than two, so the control isn't there rather than sitting greyed. */}
        {options.length > 1 && (
          <Button
            internalType="outline"
            className="h-auto ml-auto flex items-center gap-1 py-1 px-2 text-xs text-muted"
            onClick={onSort}
          >
            <Sort />
            Sort
          </Button>
        )}
      </div>
      <div>
        {options.map((option, index) => (
          <div
            key={index}
            data-testid={`option-row-${index}`}
            className={`flex items-center gap-2 border border-divider rounded-lg bg-surface hover:bg-surface-sunken px-2 mb-1.5 ${
              dragIndex === index ? 'opacity-50' : ''
            }`}
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleDrop(index)}
          >
            <span
              className="flex text-faint cursor-grab"
              title="Drag to reorder"
              draggable
              onDragStart={() => setDragIndex(index)}
              onDragEnd={() => setDragIndex(null)}
            >
              <Drag />
            </span>
            <Input
              aria-label={`Option ${index + 1}`}
              type="text"
              value={option}
              className="flex-1 min-w-0 border-0 rounded-none bg-transparent px-0 py-1.5 font-semibold focus:ring-0"
              onChange={(event) => onUpdate(index, event.target.value)}
            />
            <Button
              internalType="transparent"
              aria-label="Delete option"
              className="px-1"
              onClick={() => onRemove(index)}
            >
              <span className="flex text-red">
                <Cross />
              </span>
            </Button>
          </div>
        ))}

        {draft !== null && (
          <div
            data-testid="option-row-new"
            className="flex items-center gap-2 border border-divider rounded-lg bg-surface hover:bg-surface-sunken px-2 mb-1.5"
          >
            {/* Kept for alignment with the rows above; there is nothing to reorder until it's an option. */}
            <span className="flex text-divider">
              <Drag />
            </span>
            <Input
              ref={draftInputRef}
              aria-label="New option"
              type="text"
              value={draft}
              placeholder="Name this option…"
              className="flex-1 min-w-0 border-0 rounded-none bg-transparent px-0 py-1.5 font-semibold focus:ring-0"
              onChange={(event) => setDraft(event.target.value)}
              onBlur={handleDraftBlur}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  commitDraft(true)
                }
                if (event.key === 'Escape') {
                  event.preventDefault()
                  cancelDraft()
                }
              }}
            />
            <Button internalType="transparent" aria-label="Discard option" className="px-1" onClick={cancelDraft}>
              <span className="flex text-red">
                <Cross />
              </span>
            </Button>
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-1">
        <Button
          internalType="outline"
          className="h-auto flex items-center gap-1 py-2 px-3 text-muted"
          data-testid="add-option"
          onClick={() => setDraft('')}
        >
          <Plus />
          Add option
        </Button>
        {offersSuggestions && (
          // A link rather than a second button: taking values the table already holds is a shortcut
          // beside the main action, not an equal to it.
          <Button
            internalType="transparent"
            className="h-auto ml-auto flex items-center gap-1 py-2 px-0 text-blue hover:bg-transparent hover:underline"
            data-testid="add-values-from-table"
            onClick={openSuggestions}
          >
            Add from table
            <ChevronDown />
          </Button>
        )}
      </div>

      {onExpandOptionsChange && (
        <div className="flex items-center gap-1 mt-4">
          <Checkbox
            checked={expandOptionsViaCellEntry !== false}
            onChange={(event) => onExpandOptionsChange(event.target.checked)}
          >
            Allow new options from typing
          </Checkbox>
          <InfoHint content={EXPAND_OPTIONS_HELP} />
        </div>
      )}

      {offersSuggestions && suggestionsMenu.open && (
        <Menu
          id="manual-option-suggestions"
          nested
          menuState={suggestionsMenu}
          setMenuState={setSuggestionsMenu}
          width={SUGGESTIONS_MENU_WIDTH}
          zIndex={3100}
        >
          <div className="p-2.5 whitespace-normal">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm text-muted">
                {suggestions.length} unique value{suggestions.length === 1 ? '' : 's'}
              </span>
              <Button
                internalType="outline"
                className="h-auto ml-auto py-1 px-2 text-xs text-muted"
                onClick={takeAllSuggestions}
              >
                Add all
              </Button>
            </div>
            <div>
              {suggestions.map((value) => (
                <div
                  key={value}
                  role="button"
                  tabIndex={0}
                  className="flex items-center gap-2 rounded cursor-pointer hover:bg-surface-sunken py-1.5 px-2"
                  onClick={() => takeSuggestion(value)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return
                    event.preventDefault()
                    takeSuggestion(value)
                  }}
                >
                  <span className="flex text-faint shrink-0">
                    <Plus />
                  </span>
                  <span className="truncate">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </Menu>
      )}
    </div>
  )
}

export default ColumnManualOptions
